/**
 * @file AddressEditDialog.tsx
 * @description Создание / редактирование адреса с DaData suggest (phase08.5 shadcn).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  addresses,
  type AddressDto,
  type AddressKind,
} from "../../lib/api-customers";

interface DadataAddrSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    postal_code?: string;
    country?: string;
    region_with_type?: string;
    city_with_type?: string;
    settlement_with_type?: string;
    street_with_type?: string;
    house?: string;
    block?: string;
    flat?: string;
    fias_id?: string;
    kladr_id?: string;
    geo_lat?: string;
    geo_lon?: string;
  };
}

interface Props {
  open: boolean;
  customerId: string;
  address: AddressDto | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Вид адреса — общий `SelectField` (phase19.31: нативный список запрещён). */
const KIND_OPTIONS: { value: AddressKind; label: string }[] = [
  { value: "SHIPPING", label: "Доставка" },
  { value: "BILLING", label: "Платёжный" },
  { value: "PICKUP", label: "Самовывоз" },
];

export function AddressEditDialog({
  open,
  customerId,
  address,
  onClose,
  onSaved,
}: Props) {
  const notify = useNotify();
  const [kind, setKind] = useState<AddressKind>("SHIPPING");
  const [displayName, setDisplayName] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [street, setStreet] = useState("");
  const [building, setBuilding] = useState("");
  const [apartment, setApartment] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [fiasId, setFiasId] = useState("");
  const [kladrId, setKladrId] = useState("");
  const [geoLat, setGeoLat] = useState("");
  const [geoLon, setGeoLon] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [notes, setNotes] = useState("");
  const [dadataSynced, setDadataSynced] = useState(false);

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<DadataAddrSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (address) {
      setKind(address.kind);
      setDisplayName(address.displayName ?? "");
      setFullAddress(address.fullAddress);
      setRegion(address.region ?? "");
      setCity(address.city ?? "");
      setStreet(address.street ?? "");
      setBuilding(address.building ?? "");
      setApartment(address.apartment ?? "");
      setPostalCode(address.postalCode ?? "");
      setFiasId(address.fiasId ?? "");
      setKladrId(address.kladrId ?? "");
      setGeoLat(address.geoLat ?? "");
      setGeoLon(address.geoLon ?? "");
      setIsDefault(address.isDefault);
      setNotes(address.notes ?? "");
      setDadataSynced(address.dadataSyncedAt !== null);
      setQuery(address.fullAddress);
    } else {
      setKind("SHIPPING");
      setDisplayName("");
      setFullAddress("");
      setRegion("");
      setCity("");
      setStreet("");
      setBuilding("");
      setApartment("");
      setPostalCode("");
      setFiasId("");
      setKladrId("");
      setGeoLat("");
      setGeoLon("");
      setIsDefault(false);
      setNotes("");
      setDadataSynced(false);
      setQuery("");
    }
  }, [address]);

  useEffect(() => {
    if (query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      setSearching(true);
      addresses
        .suggest(query.trim(), 10)
        .then((res) => setSuggestions(res.suggestions as DadataAddrSuggestion[]))
        .catch(() => setSuggestions([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  function pickSuggestion(s: DadataAddrSuggestion) {
    setFullAddress(s.value);
    setQuery(s.value);
    setRegion(s.data.region_with_type ?? "");
    setCity(s.data.city_with_type ?? s.data.settlement_with_type ?? "");
    setStreet(s.data.street_with_type ?? "");
    setBuilding([s.data.house, s.data.block].filter(Boolean).join(" ") || "");
    setApartment(s.data.flat ?? "");
    setPostalCode(s.data.postal_code ?? "");
    setFiasId(s.data.fias_id ?? "");
    setKladrId(s.data.kladr_id ?? "");
    setGeoLat(s.data.geo_lat ?? "");
    setGeoLon(s.data.geo_lon ?? "");
    setDadataSynced(true);
    setShowSuggestions(false);
  }

  async function onSubmit() {
    setError(null);
    if (!fullAddress.trim()) {
      setError("Адрес обязателен.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind,
        displayName: displayName.trim() || null,
        fullAddress: fullAddress.trim(),
        country: "Россия",
        region: region.trim() || null,
        city: city.trim() || null,
        street: street.trim() || null,
        building: building.trim() || null,
        apartment: apartment.trim() || null,
        postalCode: postalCode.trim() || null,
        fiasId: fiasId.trim() || null,
        kladrId: kladrId.trim() || null,
        geoLat: geoLat.trim() || null,
        geoLon: geoLon.trim() || null,
        isDefault,
        notes: notes.trim() || null,
        dadataSynced,
      } as const;
      if (address) {
        await addresses.update(address.id, payload);
      } else {
        await addresses.create(customerId, payload);
      }
      notify.success(address ? "Сохранено" : "Адрес добавлен");
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось сохранить";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{address ? "Редактировать адрес" : "Новый адрес"}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="a-kind">Тип адреса</Label>
            <SelectField
              id="a-kind"
              ariaLabel="Тип адреса"
              value={kind}
              onChange={(value) => setKind(value as AddressKind)}
              options={KIND_OPTIONS}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="a-display">Название (опционально)</Label>
            <Input
              id="a-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Дом / Офис / Дача"
            />
          </div>

          <div className="flex flex-col gap-1.5 relative">
            <Label htmlFor="a-full">Адрес *</Label>
            <Input
              id="a-full"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setFullAddress(e.target.value);
                setDadataSynced(false);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              autoComplete="off"
            />
            <p className="text-xs text-on-surface-variant">
              Начните вводить — DaData предложит варианты
            </p>
            {showSuggestions && suggestions.length > 0 ? (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-surface-lowest rounded-lg [box-shadow:0_8px_24px_rgba(42,47,26,0.08),inset_0_0_0_1px_var(--outline-variant)] max-h-60 overflow-y-auto">
                {searching ? (
                  <div className="px-3 py-2 text-sm text-on-surface-muted">Ищем…</div>
                ) : (
                  suggestions.map((s) => (
                    <button
                      key={s.data.fias_id ?? s.value}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSuggestion(s);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-surface-low transition-colors"
                    >
                      <div className="text-sm text-on-surface">{s.value}</div>
                      {s.data.postal_code ? (
                        <div className="text-xs text-on-surface-variant">
                          Индекс {s.data.postal_code}
                        </div>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="a-postal">Индекс</Label>
              <Input
                id="a-postal"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="a-city">Город</Label>
              <Input
                id="a-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="a-street">Улица</Label>
              <Input
                id="a-street"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
              />
            </div>
            <div className="w-24 flex flex-col gap-1.5">
              <Label htmlFor="a-bld">Дом</Label>
              <Input
                id="a-bld"
                value={building}
                onChange={(e) => setBuilding(e.target.value)}
              />
            </div>
            <div className="w-24 flex flex-col gap-1.5">
              <Label htmlFor="a-flat">Кв./офис</Label>
              <Input
                id="a-flat"
                value={apartment}
                onChange={(e) => setApartment(e.target.value)}
              />
            </div>
          </div>

          <label className="flex min-h-12 items-center gap-2 cursor-pointer">
            <Checkbox
              checked={isDefault}
              onCheckedChange={(c) => setIsDefault(c === true)}
            />
            <span className="text-sm text-on-surface">
              По умолчанию для этого типа
            </span>
          </label>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="a-notes">Комментарий</Label>
            <Textarea
              id="a-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={saving}>
            {saving ? "Сохраняем…" : address ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
